import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withRouter } from 'react-router';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { FlexContainer } from '../../styled/layout.styled';
import SideNavMenuItem from './SideNavMenuItem';
import Button from '../reusable/Button';
import dashboardIcon from './assets/Dashboard.svg';
import campaignsIcon from './assets/Campaigns.svg';
import commissionPlansIcon from './assets/Pie Chart.svg';
import referredUsersIcon from './assets/Star Badge.svg';
import transactionsIcon from './assets/Invoice.svg';
import resourcesIcon from './assets/Images.svg';
import accountSettingsIcon from './assets/Settings.svg';
import dashboardMenuIcon from './assets/Dashboard Menu Icon.svg';
import useModal from '../../hooks/useModal';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import useScrollingElement from '../../hooks/useScrollingElement';
import CreateCampaignModal from '../reusable/CreateCampaignModal';
import { campaignSchema } from '../Campaigns/config';

const StyledFlexContainer = styled(FlexContainer)`
  width: 258px;
  height: 595px;
  flex-direction: column;
  align-items: center;
  border-radius: 4px;
  background-color: #1a0949;
  padding: 24px;
  z-index: 1;

  @media only screen and (max-width: 1157px) {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    display: flex;
    ${({ openCloseMenu }) => openCloseMenu
    ? css`
            transform: translateX(0px);
          `
    : css`
            transform: translateX(-600px);
          `};
    ${({ openCloseMenu }) => openCloseMenu
    ? css`
            opacity: 1;
          `
    : css`
            opacity: 0;
          `};
    transition: all 0.3s ease-in;
    z-index: 369;
    width: 360px;
    height: auto;
    overflow-y: hidden;
    padding: 50px 0 0 0;
    border-radius: 0;
    justify-content: flex-start;
    align-items: center;
  }
  }

  @media only screen and (max-width: 568px) {
    position: fixed;
    top: 0;
    left: 0;
    display: flex;
    ${({ openCloseMenu }) => openCloseMenu
    ? css`
            transform: translateX(0px);
          `
    : css`
            transform: translateX(-600px);
          `};
    ${({ openCloseMenu }) => openCloseMenu
    ? css`
            opacity: 1;
          `
    : css`
            opacity: 0;
          `};
    transition: all 0.3s ease-in;
    bottom: 0;
    z-index: 369;
    width: 100%;
    height: 100vh;
    padding: 0;
    overflow-y: hidden;
    border-radius: 0;
    justify-content: center;
    align-items: center;
  }
`;

const StyledLine = styled.div`
  width: 210px;
  height: 1px;
  opacity: 0.2;
  background-color: #c7baf1;
  margin: 16px 0 32px 0;
`;

const StyledMenuTrigger = styled.div`
  display: none;

  @media only screen and (max-width: 1157px) {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 54px;
    height: 38px;
    background-color: #f0ecff;
    cursor: pointer;
    position: absolute;
    left: 0;
    top: 56px;
    border-radius: 0 10px 10px 0;
    z-index: ${({ zIndex }) => (zIndex ? '370' : '369')};
  }
  @media only screen and (max-width: 568px) {
    display: flex;
    width: 54px;
    height: 38px;
    background-color: #f0ecff;
    cursor: pointer;
    position: fixed;
    left: 0;
    top: 56px;
    border-radius: 0 10px 10px 0;
    z-index: ${({ zIndex }) => (zIndex ? '370' : '369')};
  }
`;

const routes = [
  {
    id: 0,
    name: 'Dashboard',
    location: 'dashboard',
    image: dashboardIcon,
  },
  {
    id: 1,
    name: 'Campaigns',
    location: 'campaigns',
    image: campaignsIcon,
  },
  {
    id: 2,
    name: 'Commission Plans',
    location: 'commission-plans',
    image: commissionPlansIcon,
  },
  {
    id: 3,
    name: 'Referred Users',
    location: 'referred-users',
    image: referredUsersIcon,
  },
  {
    id: 4,
    name: 'Transactions',
    location: 'transactions',
    image: transactionsIcon,
  },
  {
    id: 5,
    name: 'Resources',
    location: 'resources',
    image: resourcesIcon,
  },
  {
    id: 6,
    name: 'Account Settings',
    location: 'account-settings',
    image: accountSettingsIcon,
  },
];

const SideNav = ({ match: { path }, history }) => {
  const [toggler, setToggler] = useState(false);
  const [open, onOpen, onClose] = useModal();
  const {
    register,
    handleSubmit,
    errors,
    formState: { isValid },
    reset,
  } = useForm({
    resolver: yupResolver(campaignSchema),
    mode: 'all',
  });
  const ref = useRef();
  useOnClickOutside(ref, () => setToggler(false));
  if (window.innerWidth <= 568) {
    useScrollingElement(toggler);
  } else {
    useScrollingElement(false);
  }

  const onClickHandler = () => {
    setToggler(prev => !prev);
  };
  return (
    <>
      <StyledMenuTrigger onClick={() => onClickHandler()} zIndex={toggler}>
        {toggler ? (
          <svg width="22" height="14" viewBox="0 0 22 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="18" height="2" rx="1" fill="#22096C" />
            <rect y="12" width="22" height="2" rx="1" fill="#22096C" />
            <rect y="6" width="12" height="2" rx="1" fill="#22096C" />
          </svg>
        ) : (
          <img src={dashboardMenuIcon} alt="menu-icon" />
        )}
      </StyledMenuTrigger>
      <StyledFlexContainer ref={ref} openCloseMenu={toggler}>
        {routes.map(({
          id, name, location, image,
        }) => (
          <SideNavMenuItem key={id} history={history} name={name} location={location} path={path} image={image} />
        ))}
        <StyledLine />
        <Button width="210px" buttonShape="bordered" onClick={onOpen} hoverTextColor="#f0ecff">
          Create New Campaign
        </Button>
      </StyledFlexContainer>
      <CreateCampaignModal
        open={open}
        onClose={onClose}
        register={register}
        handleSubmit={handleSubmit}
        reset={reset}
        errors={errors}
        name={{ first: 'name', second: 'code' }}
        label={{ first: 'Campaign Name', second: 'Campaign Code' }}
        isValid={isValid}
      />
    </>
  );
};

SideNav.propTypes = {
  path: PropTypes.string,
  match: PropTypes.object.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

SideNav.defaultProps = {
  path: '',
};

export default withRouter(SideNav);
